import { useState } from "react";
import { fromDate, parseTime, Time } from "@internationalized/date";
import { toast } from "sonner";
import {
	mutationOptions,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";

import { staffPortalApi } from "../api";
import type { Shift, StaffMember } from "../types";
import type { EarlyLeaveChoice } from "./early-leave-reason-picker";
import { PIN_REJECTED_MESSAGE, pinFailureMessage } from "./pin-failure-message";

// The check-in/out state machine behind CheckInOutPinOnly, in its own module so it can be tested
// without driving the keypad (HA-768).

const errorMessages = {
	noStaffFound: PIN_REJECTED_MESSAGE,
	noActiveShift: "No scheduled shift found.",
	shiftStarted: "You have already checked out of this shift.",
	// HA-782: only a real PIN failure says "Invalid Staff ID or PIN"; these name what happened.
	notRecorded:
		"We couldn't record your check-in or check-out. Please try again or ask a manager.",
};

/** HA-782: the server answered, and refused to record the check-in or out; `message` is its reason. */
export class CheckInOutRefused extends Error {
	constructor(message: string) {
		super(message);
		this.name = "CheckInOutRefused";
	}
}

/**
 * HA-768 / HA-192 AC5–6 — the API wrappers resolve `{ success: false }` on a refusal rather than
 * throwing, so a caller that only awaited them showed "Checked in at …" whatever the server said,
 * and a timesheet could be wrong with the till saying otherwise. This throws on a refusal, so the
 * confirmation is only ever shown for a check-in or check-out the server recorded, and a refusal
 * takes the component's existing failure path.
 */
function recorded<T extends { success: boolean }>(
	result: T,
): Extract<T, { success: true }> {
	if (!result.success) {
		const reason = "error" in result ? result.error : undefined;
		throw new CheckInOutRefused(
			typeof reason === "string" && reason ? reason : errorMessages.notRecorded,
		);
	}
	return result as Extract<T, { success: true }>;
}

export function useCheckInOut(data: {
	staffMembers: StaffMember[];
	shifts: Shift[];
	venueId: string;
	tenant: string;
	timeZone: string;
}) {
	const { staffMembers, shifts, venueId, tenant, timeZone } = data;
	const queryClient = useQueryClient();

	const [status, setStatus] = useState<
		| "idle"
		| "checking-pin"
		| "success"
		| "error"
		| "checkout-early"
		| "pin-reset-required"
	>("idle");

	const [showSuccess, setShowSuccess] = useState(false);
	const [showCheckoutSuccess, setShowCheckoutSuccess] = useState(false);
	const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(
		staffMembers.find((x) => x.id === shifts[0]?.staffMemberId) || null,
	);
	const [errorMessage, setErrorMessage] = useState<string | undefined>(
		undefined,
	);
	const [shouldShake, setShouldShake] = useState(false);
	const [currentShift, setCurrentShift] = useState<Shift | null>(null);

	const checkPin = useMutation({
		...staffPortalApi.checkPinOptions(tenant, venueId),
	});

	const getOptions = staffPortalApi.getOptions(tenant, venueId);

	const checkInOut = useMutation(
		mutationOptions({
			...staffPortalApi.checkInOutOptions(tenant, venueId),
			onSettled(response, _error, _variables, _onMutateResult, context) {
				if (response && response.success) {
					// context.client.setQueryData(getOptions.queryKey, (p) => {
					// 	return {
					// 		...p!,
					// 		shifts: response.response!.shifts,
					// 	};
					// });
					context.client.invalidateQueries({ queryKey: getOptions.queryKey });
				}
			},
		}),
	);

	const recordEarlyLeave = useMutation({
		...staffPortalApi.recordEarlyLeaveOptions(tenant, venueId),
		onSuccess: (d) => {
			if (d.success) {
				queryClient.invalidateQueries({
					queryKey: [tenant],
				});
			}
		},
	});

	const setPin = useMutation({
		...staffPortalApi.setPinOptions(tenant, venueId),
		onSuccess: (d) => {
			if (d.success) {
				queryClient.invalidateQueries({
					queryKey: [tenant],
				});
			}
		},
	});

	const findTodayShift = (
		staffId: string | undefined | null,
		shiftsData = shifts,
	) => {
		return shiftsData.find(
			(shift) =>
				shift.staffMemberId === staffId &&
				["Completed", "CompletedEarly", "Cancelled"].indexOf(shift.status) ===
					-1,
		);
	};

	function shake() {
		setShouldShake(true);
		setTimeout(() => setShouldShake(false), 600);
	}

	function setError(message: string) {
		setErrorMessage(message);
		setStatus("error");
		shake();
	}

	function settingPin() {
		setStatus("checking-pin");
		setErrorMessage(undefined);
		setSelectedStaff(null);
		setCurrentShift(null);
	}

	function success() {
		setStatus("success");
		setShowSuccess(true);
		setErrorMessage(undefined);
	}

	function checkoutSuccess() {
		setStatus("success");
		setShowCheckoutSuccess(true);
		setErrorMessage(undefined);
	}

	async function handleVerifyPinAndChangeStatus(pinData: {
		staffId: string;
		pin: string;
	}) {
		settingPin();

		const { pin } = pinData;

		const staff = staffMembers.find(
			(s) => s.staffNumber === parseInt(pinData.staffId, 10),
		);

		if (!staff) {
			setError(errorMessages.noStaffFound);

			return false;
		}

		setSelectedStaff(staff);

		// HA-829: the PIN buys a two-minute, single-use ticket for this staff member, spent by the one
		// check-in, check-out or PIN change that follows.
		async function getTicketForPin() {
			return await checkPin.mutateAsync({
				registeredUserId: staff?.registeredUserId,
				pin,
			});
		}

		try {
			const result = await getTicketForPin();

			if (!result.success) {
				// HA-782 / HA-804: only the server's 401 is a wrong PIN; a 403, a 500 or a dropped
				// connection says what it is.
				setError(pinFailureMessage(result));
				return false;
			}

			if (result.data.isTemporary) {
				setStatus("pin-reset-required");
				shake();
				return false;
			}

			const staffMemberId = staff.id;
			const shift = findTodayShift(staffMemberId);

			if (!shift) {
				const staffData = recorded(
					await checkInOut.mutateAsync({
						ticket: result.data.ticket,
						staffMemberId: staff.id,
						date: new Date(),
					}),
				);

				const newShift =
					findTodayShift(staffMemberId, staffData.response.shifts) || null;

				setCurrentShift(newShift);

				success();
				return true;
			}

			setCurrentShift(shift);

			if (shift.status === "Scheduled") {
				recorded(
					await checkInOut.mutateAsync({
						ticket: result.data.ticket,
						shiftId: shift.id,
						staffMemberId: staff.id,
						date: new Date(),
					}),
				);

				success();
				return true;
			}

			if (shift.status === "CheckedIn") {
				const now = fromDate(new Date(), timeZone);
				const nowTime = new Time(now.hour, now.minute);
				const shiftTime = parseTime(shift.endTime || "23:59");

				if (!shift.isAdHoc && nowTime.compare(shiftTime) < 0) {
					setStatus("checkout-early");
					shake();
					return false;
				}

				recorded(
					await checkInOut.mutateAsync({
						ticket: result.data.ticket,
						shiftId: shift.id,
						staffMemberId: staff.id,
						date: new Date(),
					}),
				);

				checkoutSuccess();

				return true;
			}

			setError("Shift already completed for today.");
			setStatus("error");
			return false;
		} catch (error) {
			// HA-782: every failure past the PIN check used to say "Invalid Staff ID or PIN". A
			// refusal shows the server's reason (or the network message the API wrapper gives a
			// dropped connection); anything else says the check-in wasn't recorded.
			setStatus("error");
			setError(
				error instanceof CheckInOutRefused
					? error.message
					: errorMessages.notRecorded,
			);
			shake();

			return false;
		}
	}

	// HA-368: ending the shift early, with the reason picked (and a note with Other).
	async function handleCheckout(choice: EarlyLeaveChoice) {
		if (!selectedStaff) {
			toast.error("No staff member selected");
			return false;
		}

		if (!currentShift) {
			toast.error("No active shift found");
			return false;
		}

		// HA-829: the early leave spends the ticket the PIN check bought.
		const response = await recordEarlyLeave.mutateAsync({
			shiftId: currentShift.id,
			staffMemberId: selectedStaff.id,
			ticket: checkPin.data?.success ? checkPin.data.data.ticket : undefined,
			reason: choice.reason,
			...(choice.note ? { note: choice.note } : {}),
		});

		// HA-768 / HA-192 AC6: confirm only an early leave the server recorded.
		if (!response.success) {
			toast.error(response.error || "The early leave was not recorded");
			return false;
		}

		checkoutSuccess();

		return true;
	}

	async function handlePinSet(resetData: {
		newPin: string;
		confirmPin: string;
	}) {
		if (!selectedStaff) {
			toast.error("No staff member selected");
			return false;
		}

		// HA-829: the ticket from the PIN check says whose PIN this is.
		const ticket = checkPin.data?.success
			? checkPin.data.data.ticket
			: undefined;
		if (!ticket) {
			toast.error("Please enter your PIN again");
			return false;
		}

		const response = await setPin.mutateAsync({
			ticket,
			staffMemberId: selectedStaff.id,
			pin: resetData.newPin,
		});

		if (response.success) {
			toast.success("PIN reset successfully!");
			setStatus("idle");
			setErrorMessage(undefined);
			setSelectedStaff(null);
			setCurrentShift(null);
			return true;
		}

		shake();
		toast.error(response.error || "Failed to reset PIN");

		return false;
	}

	return {
		status,
		setError,
		errorMessage,
		selectedStaff,
		setSelectedStaff,
		showSuccess,
		shouldShake,
		handleVerifyPinAndChangeStatus,
		currentShift,
		showCheckoutSuccess,
		reset: () => {
			setStatus("idle");
			setErrorMessage(undefined);
			setShowSuccess(false);
			setSelectedStaff(null);
			setCurrentShift(null);
		},

		checkout: handleCheckout,
		setPin: handlePinSet,
	};
}
